/**
 * ===================================================================================
 * 📁 경로: services/frontend/src/pages/ActionPlansByReportPage.js
 * 📌 프로그램명: 리포트별 건강 목표 및 실천 계획 조회 페이지
 * 🧩 개요:
 * - 특정 AI 리포트에서 파생된 모든 '건강 목표(Health Goals)'와 각 목표에 속한
 * '실천 계획(Action Plans)'을 계층적으로 시각화하여 보여주는 페이지입니다.
 * - 사용자는 이 페이지를 통해 AI가 제안한 전체적인 건강 관리 전략을 한눈에 파악하고,
 * 각 실천 계획에 대한 로그를 기록하거나 조회하는 페이지로 이동할 수 있습니다.
 * ===================================================================================
 */
import React, { useEffect, useMemo, useState } from 'react';
import styles from './ActionPlansByReportPage.module.css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ActionPlanLogModal from '../components/ActionPlanLogModal.jsx';
import MessageModal from '../components/MessageModal';

/**
 * [Why]
 * AI가 생성한 분석 리포트의 최종 결과물인 '실천 계획'을 사용자가 명확하게 인지하고
 * 관리할 수 있도록 하는 핵심 UI를 제공합니다. 이는 단순한 정보 조회를 넘어, 사용자가
 * AI의 제안을 실제 행동으로 옮기도록 유도하는 중요한 연결고리 역할을 합니다.
 *
 * [How]
 * - **데이터 조회**: 페이지 진입 시 URL 파라미터(`reportId`)를 이용해 해당 리포트의
 * 상세 데이터를 비동기적으로 조회합니다. 여기에는 건강 목표와 실천 계획이 모두 포함됩니다.
 * - **상태 관리**: `useState` 훅을 사용하여 로딩/에러 상태, 필터 조건(카테고리, 상태),
 * 모달 표시 여부 등 페이지의 모든 동적 상태를 관리합니다.
 * - **데이터 시각화**: 조회된 데이터를 'GoalCard'와 'PlanCard'라는 중첩된 컴포넌트 구조로
 * 렌더링하여, 목표와 실천 계획 간의 계층 관계를 명확하게 보여줍니다.
 * - **필터링**: 사용자가 선택한 카테고리(`categoryFilter`)나 상태(`statusFilter`)에 따라
 * 표시되는 목표와 계획을 동적으로 필터링하여 보여줍니다.
 * - **상호작용**: 사용자가 '로그 추가' 버튼을 클릭하면 `ActionPlanLogModal`을 띄워
 * 새로운 건강 활동 기록을 생성할 수 있도록 합니다.
 *
 * @component
 * @returns {JSX.Element} ActionPlansByReportPage 컴포넌트의 JSX 엘리먼트.
 */
function ActionPlansByReportPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isApp = location?.pathname?.startsWith('/app');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 리포트 및 실천 계획 조회 오류를 모달창으로 안내하기 위한 상태
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // not_started | in_progress | done | all
  const [isMobile, setIsMobile] = useState(false);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // 상태/카테고리 라벨 매핑
  const statusLabel = {
    all: '전체',
    not_started: '시작전',
    in_progress: '진행중',
    done: '완료',
    completed: '성공',
    paused: '일시중지',
    superseded: '대체됨',
  };

  // 카테고리별 기본기간(일) - 백엔드와 동일 정책
  const defaultDurationByCategory = (cat) => {
    const key = (cat || '').toLowerCase();
    if (key === 'dietary_habit') return 28;
    if (key === 'physical_activity' || key === 'metabolic_care') return 90;
    if (key === 'sleep_management' || key === 'mental_care') return 56;
    return 56; // 기본값
  };

  // target_date 없을 때 start_date + 기간으로 보정
  const getTargetDate = (plan) => {
    // 1) 다양한 타겟/마감 필드 우선 사용
    let raw = plan?.target_date || plan?.targetDate || plan?.end_date || plan?.endDate || plan?.due_date || plan?.dueDate;
    if (typeof raw === 'string') raw = raw.trim();
    if (raw) return raw;

    // 2) 시작일 + 기간(있으면 그 값, 없으면 카테고리 기본기간)
    let s = plan?.start_date || plan?.startDate;
    if (typeof s === 'string') s = s.trim();
    if (!s) return undefined;
    // 안전 파싱
    const isDateOnly = typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
    const start = isDateOnly ? new Date(`${s}T00:00:00Z`) : new Date(s);
    if (isNaN(start.getTime())) return undefined;

    // 기간 일수: period_days/duration_days 우선, 없으면 카테고리 기본기간
    let days = plan?.period_days ?? plan?.periodDays ?? plan?.duration_days ?? plan?.durationDays;
    if (typeof days === 'string') days = days.trim();
    if (typeof days === 'string' && /^-?\d+$/.test(days)) days = parseInt(days, 10);
    if (typeof days !== 'number' || isNaN(days)) {
      days = defaultDurationByCategory(plan?.category);
    }
    const targetMs = start.getTime() + days * 24 * 60 * 60 * 1000;
    return new Date(targetMs).toISOString();
  };
  const categoryLabel = {
    metabolic_care: '대사관리',
    dietary_habit: '식습관',
    physical_activity: '신체활동',
    sleep_management: '수면',
    mental_care: '멘탈케어',
  };
  const frequencyLabel = {
    daily: '매일',
    weekly: '매주',
    monthly: '매월',
    x1_per_week: '주1회',
    x2_per_week: '주2회',
    x3_per_week: '주3회',
    x4_per_week: '주4회',
    x5_per_week: '주5회',
  };

  // 컴포넌트 마운트 시 reportId를 기반으로 리포트 상세 데이터를 API로부터 조회합니다.
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const { data } = await axios.get(`${apiUrl}/api/v1/reports/${reportId}`);
        if (mounted) setReport(data || null);
      } catch (e) {
        const backendDetail = e?.response?.data?.detail || e?.response?.data?.message;
        // [추가]
        // - 서버 미기동/접속 불가 등 네트워크 레벨 오류는 'Network Error'로 표시될 수 있어,
        //   사용자 친화 문구로 치환합니다.
        const msg = (e && e.message === 'Network Error' && !e.response)
          ? '서비스 준비중입니다. 잠시후에 다시 시도하시기 바랍니다.'
          : (backendDetail || e?.message || '실천 계획 조회 중 오류가 발생했습니다.');
        if (mounted) {
          setError(msg);
          setIsErrorModalOpen(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [reportId]);

  /**
   * [Why]
   * 사용자가 로그를 추가/수정한 후, 변경된 내용(예: 실천 계획 상태)을 화면에 즉시
   * 반영하기 위해 리포트 데이터를 다시 조회하는 함수입니다.
   *
   * [How]
   * 기존 `load` 함수의 로직과 동일하게 리포트 상세 조회 API를 호출하여 `report` 상태를 갱신합니다.
   */
  const reloadReport = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const { data } = await axios.get(`${apiUrl}/api/v1/reports/${reportId}`);
      setReport(data || null);
    } catch (_) {}
  };

  // 모바일 폭 감지 (간단 기준: 640px 이하)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const healthGoals = report?.health_goals;
  const goals = useMemo(
    () => (Array.isArray(healthGoals) ? healthGoals : []),
    [healthGoals],
  );

  // 필터링 옵션을 동적으로 생성하기 위해, 현재 리포트에 포함된 모든 카테고리를 추출합니다.
  const categories = useMemo(() => {
    const set = new Set();
    goals.forEach(g => {
      if (g.category) set.add(g.category);
      (g.action_plans || []).forEach(p => p.category && set.add(p.category));
    });
    return ['all', ...Array.from(set)];
  }, [goals]);

  if (loading) return <div style={{ padding: 16 }}>불러오는 중...</div>;
  if (!report && !loading) {
    return (
      <MessageModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="알림"
        message={error || '실천 계획 조회 중 오류가 발생했습니다.'}
        type="error"
      />
    );
  }

  const badge = (text, color, clamp = false) => (
    <span className={clamp ? styles.badgeClamp : undefined} style={{
      display: 'inline-block',
      padding: isMobile ? '1px 6px' : '2px 8px',
      borderRadius: 999,
      fontSize: isMobile ? 11 : 12,
      background: color,
      color: '#fff',
      marginRight: isMobile ? 6 : 8,
      lineHeight: 1.2,
    }}>{text}</span>
  );

  const statusColor = (s) => {
    const key = (s || '').toLowerCase();
    if (key === 'in_progress') return '#3b82f6'; // 파랑
    if (key === 'done' || key === 'completed' || key === 'success') return '#10b981'; // 초록
    if (key === 'paused') return '#f59e0b'; // 주황(일시중지)
    if (key === 'superseded') return '#9ca3af'; // 회색(대체됨)
    return '#6b7280'; // 회색 (준비중/기타)
  };

  /**
   * [Why]
   * AI가 제안한 개별 '실천 계획' 항목을 시각적으로 표현하는 재사용 가능한 컴포넌트입니다.
   * 복잡한 페이지 구조를 작은 단위로 분리하여 코드의 가독성과 유지보수성을 높입니다.
   *
   * @param {{plan: object}} props - PlanCard 컴포넌트가 받는 데이터 객체입니다.
   * @property {object} props.plan - 표시할 실천 계획(ActionPlan) 데이터 객체.
   * @returns {JSX.Element} PlanCard 컴포넌트의 JSX 엘리먼트.
   */
  const PlanCard = ({ plan }) => (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      padding: isMobile ? 10 : 12,
      marginBottom: 10,
      background: '#fff',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
    }}>
      {/* 제목 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 8, WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', letterSpacing: '0.1px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 6, background: '#e0f2fe', color: '#0369a1' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
          </span>
          {plan.suggestion || '실천 항목'}
        </div>
      </div>
      {/* 칩 컨테이너 */}
      <div className={styles.chipContainer} style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 6 : 8,
        marginTop: 8,
        padding: isMobile ? '4px 6px' : '6px 8px',
        background: '#f1f5f9',
        borderRadius: 10,
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {plan.category && badge(categoryLabel[plan.category] || plan.category, '#6366f1', true)}
          {plan.frequency && badge(frequencyLabel[plan.frequency] || plan.frequency, '#10b981', true)}
          {plan.status && badge(statusLabel[(plan.status || '').toLowerCase()] || plan.status, statusColor(plan.status))}
        </div>
        <div style={{ marginLeft: isMobile ? 6 : 8, flexShrink: 0 }}>
          {/* D-day 배지 (오른쪽 정렬) */}
          {(() => {
            const t = getTargetDate(plan);
            if (!t) return badge('계속', '#9ca3af', true);
            const today = new Date();
            // 안전 파싱: 'YYYY-MM-DD'면 UTC 자정으로 해석
            const tStr = typeof t === 'string' ? t.trim() : t;
            const isDateOnly = typeof tStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(tStr);
            const target = isDateOnly ? new Date(`${tStr}T00:00:00Z`) : new Date(tStr);
            // 날짜만 비교(UTC 정규화)
            const todayDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
            const targetDate = new Date(Date.UTC(target.getFullYear(), target.getMonth(), target.getDate()));
            const diffMs = targetDate - todayDate;
            const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
            let label = '';
            if (diffDays > 0) label = `D-${diffDays}`;
            else if (diffDays === 0) label = 'D-Day';
            else label = `D+${Math.abs(diffDays)}`;
            return badge(label, diffDays >= 0 ? '#0ea5e9' : '#ef4444', true);
          })()}
        </div>
      </div>
      {plan.rationale && (
        <div style={{ color: '#6b7280', marginTop: 6, fontSize: 14 }}>{plan.rationale}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        {(() => {
          const isSuperseded = (plan.status || '').toLowerCase() === 'superseded';
          return (
            <div style={{ display: 'inline-flex', gap: 8 }}>
              <button
                onClick={() => navigate(isApp ? `/app/reports/${reportId}/logs` : `/reports/${reportId}/logs`, { state: { planId: plan.plan_id, reportId } })}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 30, padding: '6px 10px', borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: 13
                }}
                title={'기록 조회'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 21l-4.35-4.35"/><circle cx="10" cy="10" r="7"/></svg>
                기록 조회
              </button>
              <button
                disabled={isSuperseded}
                onClick={() => { if (!isSuperseded) { setSelectedPlan(plan); setLogModalOpen(true); } }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 30, padding: '6px 10px', borderRadius: 8,
                  border: isSuperseded ? '1px solid #e5e7eb' : '1px solid #6366f1',
                  background: isSuperseded ? '#f3f4f6' : 'linear-gradient(180deg,#c7d2fe 0%,#a5b4fc 100%)',
                  color: isSuperseded ? '#9ca3af' : '#1e1b4b',
                  cursor: isSuperseded ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  opacity: isSuperseded ? 0.7 : 1
                }}
                title={isSuperseded ? '대체된 계획에는 기록을 추가할 수 없습니다.' : '실천 기록 추가'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                기록 추가
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );

  const ProgressBar = ({ value }) => (
    <div style={{ width: '100%', background: '#e5e7eb', borderRadius: 999, height: 8 }}>
      <div style={{ width: `${Math.round(value * 100)}%`, height: '100%', borderRadius: 999, background: '#22c55e', transition: 'width 200ms ease' }} />
    </div>
  );

  /**
   * [Why]
   * AI가 제안한 상위 '건강 목표'와 그에 속한 여러 '실천 계획'들을 그룹화하여 보여주는
   * 재사용 가능한 컴포넌트입니다.
   *
   * @param {{goal: object}} props - GoalCard 컴포넌트가 받는 데이터 객체입니다.
   * @property {object} props.goal - 표시할 건강 목표(HealthGoal) 데이터 객체.
   * @returns {JSX.Element} GoalCard 컴포넌트의 JSX 엘리먼트.
   */
  const GoalCard = ({ goal }) => (
    <div style={{
      border: '1px solid #dbeafe',
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      background: '#f8fafc'
    }}>
      {/* 제목 행 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <h3 style={{ margin: 0, lineHeight: 1.3, fontSize: isMobile ? 18 : 20, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8, WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', letterSpacing: '0.1px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, background: '#ede9fe', color: '#5b21b6' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
          </span>
          {goal.goal_title || '건강 목표'}
        </h3>
        {typeof goal.priority === 'number' && (
          <div>
            {badge(isMobile ? String(goal.priority) : `우선순위 ${goal.priority}`, '#f97316')}
          </div>
        )}
      </div>
      {/* 태그 행 (카테고리/상태) */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 8,
        padding: '6px 8px',
        background: '#eef2ff',
        borderRadius: 10,
        border: '1px solid #e5e7eb'
      }}>
        {goal.category && badge(categoryLabel[goal.category] || goal.category, '#3b82f6')}
        {goal.status && badge(statusLabel[(goal.status || '').toLowerCase()] || goal.status, statusColor(goal.status))}
      </div>
      {goal.goal_description && (
        <div style={{ color: '#475569', marginTop: 6 }}>{goal.goal_description}</div>
      )}
      {/* 진행률 */}
      <div style={{ marginTop: 10 }}>
        {(() => {
          const plans = Array.isArray(goal.action_plans) ? goal.action_plans : [];
          const total = plans.length;
          const done = plans.filter(p => (p.status || '').toLowerCase() === 'done' || (p.status || '').toLowerCase() === 'completed').length;
          const ratio = total > 0 ? done / total : 0;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ minWidth: 90, fontSize: 12, color: '#6b7280' }}>진행률</div>
              <div style={{ flex: 1 }}><ProgressBar value={ratio} /></div>
              <div style={{ minWidth: 60, textAlign: 'right', fontSize: 12, color: '#374151' }}>{Math.round(ratio * 100)}%</div>
            </div>
          );
        })()}
      </div>
      <div style={{ marginTop: 12 }}>
        {Array.isArray(goal.action_plans) && goal.action_plans.length > 0 ? (
          goal.action_plans.map((p) => <PlanCard plan={p} key={p.plan_id} />)
        ) : (
          <div style={{ color: '#6b7280' }}>등록된 실천 계획이 없습니다.</div>
        )}
      </div>
    </div>
  );

  // 사용자가 선택한 필터 조건에 따라 화면에 표시될 목표(goals) 목록을 동적으로 계산합니다.
  const filteredGoals = goals
    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
    .map(g => {
      let visible = true;
      const catOk = categoryFilter === 'all' || [g.category, ...(g.action_plans || []).map(p => p.category)].includes(categoryFilter);
      visible = visible && catOk;
      let filteredPlans = Array.isArray(g.action_plans) ? [...g.action_plans] : [];
      if (statusFilter !== 'all') {
        filteredPlans = filteredPlans.filter(p => {
          const ps = (p.status || '').toLowerCase();
          if (statusFilter === 'done') return ps === 'done' || ps === 'completed';
          return ps === statusFilter;
        });
      }
      return { ...g, action_plans: filteredPlans, __visible: visible };
    })
    .filter(g => g.__visible);

  return (
    <div style={{ padding: 16, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h2 style={{ margin: 0 }}>건강 목표와 실천 계획</h2>
          {!isApp && (
            <button
              onClick={() => navigate(-1)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                height: 32, padding: '6px 10px', borderRadius: 999,
                border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)', cursor: 'pointer',
                fontSize: 14, lineHeight: 1
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              뒤로
            </button>
          )}
        </div>
      </div>

      {/* 필터 & 상태 탭 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: 12,
        marginBottom: 16,
        flexWrap: 'wrap'
      }}>
        <div style={{ fontSize: 14, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h10M4 18h6"/></svg>
          카테고리
        </div>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}
        >
          {categories.map(c => (
            <option key={c} value={c}>{c === 'all' ? '전체' : (categoryLabel[c] || c)}</option>
          ))}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['all','not_started','in_progress','paused','done'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                height: 32, padding: '6px 10px',
                borderRadius: 999,
                border: statusFilter === s ? '1px solid #6366f1' : '1px solid #e5e7eb',
                background: statusFilter === s ? 'linear-gradient(180deg,#c7d2fe 0%,#a5b4fc 100%)' : '#fff',
                color: statusFilter === s ? '#312e81' : '#374151',
                boxShadow: statusFilter === s ? '0 2px 6px rgba(99,102,241,0.25)' : 'none',
                cursor: 'pointer', fontSize: 14, lineHeight: 1
              }}
              aria-label={s}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {s === 'in_progress' && (<>
                  <circle cx="12" cy="12" r="9"/>
                  <path d="M12 7v5l3 3"/>
                </>)}
                {s === 'done' && <path d="M20 6L9 17l-5-5"/>}
                {s === 'not_started' && (<>
                  <rect x="8" y="7" width="3" height="10" rx="1"/>
                  <rect x="13" y="7" width="3" height="10" rx="1"/>
                </>)}
                {s === 'paused' && (<>
                  <rect x="8" y="7" width="3" height="10" rx="1"/>
                  <rect x="13" y="7" width="3" height="10" rx="1"/>
                </>)}
                {s === 'all' && <path d="M4 6h16M4 12h16M4 18h16"/>}
              </svg>
              {statusLabel[s] || s}
            </button>
          ))}
        </div>
      </div>

      {filteredGoals.length === 0 ? (
        <div style={{ color: '#6b7280' }}>등록된 건강 목표가 없습니다.</div>
      ) : (
        filteredGoals.map((g) => <GoalCard goal={g} key={g.goal_id} />)
      )}
      <ActionPlanLogModal
        open={logModalOpen}
        onClose={() => setLogModalOpen(false)}
        plan={selectedPlan}
        reportId={reportId}
        onSubmitted={reloadReport}
      />

      {/* 리포트 및 실천 계획 조회 오류 안내용 메시지 모달 */}
      <MessageModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="알림"
        message={error}
        type="error"
      />
    </div>
  );
}

export default ActionPlansByReportPage;
